/**
 * In-chat booking (AGENTS.md 11.1): consultation type → doctor → day and time → confirm.
 * Everything stays inside the window; times are shown in the patient's own time zone.
 */
import { useEffect, useMemo, useState } from "preact/hooks";
import { t, type Locale } from "@wapadesk/i18n";
import { addDays, localDate } from "@wapadesk/engine";
import type { BookingState } from "../store";
import { Payment } from "./Payment";
import { timesFor } from "../lib/slots";

export interface BookingActions {
  chooseBookingProcedures(names: string[] | undefined): void;
  chooseBookingType(id: string | undefined): void;
  chooseBookingLocation(id: string | undefined): void;
  chooseBookingDoctor(id: string | undefined): void;
  loadSlots(from: string, days?: number): Promise<void>;
  book(startAt: string, doctorId: string, atNodeId?: string): Promise<boolean>;
  holdAndPay(startAt: string, doctorId: string, atNodeId?: string): Promise<boolean>;
  awaitPaidConfirmation(): Promise<void>;
  paymentFailed(): Promise<void>;
  cancelPayment(): void;
  closeBooking(): void;
}

const DAYS = 7;

function fmtMoney(minor: number, currency: string, locale: string): string {
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency }).format(minor / 100);
  } catch {
    return `${currency} ${(minor / 100).toFixed(2)}`;
  }
}

function dayLabel(date: string, locale: string, tz: string): { top: string; bottom: string } {
  const d = new Date(`${date}T12:00:00Z`);
  const f = new Intl.DateTimeFormat(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: tz,
  });
  const parts: Record<string, string> = {};
  for (const p of f.formatToParts(d)) parts[p.type] = p.value;
  return { top: parts["weekday"] ?? "", bottom: `${parts["day"] ?? ""} ${parts["month"] ?? ""}` };
}

export function Booking({
  locale,
  booking,
  actions,
  atNodeId,
  suggested = [],
}: {
  locale: Locale;
  booking: BookingState;
  actions: BookingActions;
  atNodeId?: string | undefined;
  /** Procedures already named earlier in the chat (pre-ticked on the procedures step). */
  suggested?: string[];
}) {
  const catalogue = booking.options?.procedures ?? [];
  const [picked, setPicked] = useState<string[]>(() => booking.procedures ?? suggested);
  const patientTz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const clinicTz = booking.options?.timezone ?? patientTz;
  const type = booking.options?.types.find((x) => x.id === booking.typeId);
  // In-person types at a clinic with several locations: the patient picks the location first.
  const locations = useMemo(
    () =>
      type && type.mode === "in_person"
        ? (booking.options?.locations ?? []).filter(
            (l) => type.locationIds.length === 0 || type.locationIds.includes(l.id),
          )
        : [],
    [booking.options, type],
  );
  const needLocation = Boolean(type) && locations.length > 1 && booking.locationId === undefined;
  const locationId = booking.locationId || (locations.length === 1 ? locations[0]!.id : undefined);
  const today = localDate(new Date(), clinicTz).date;
  const from = booking.from ?? today;
  const [selected, setSelected] = useState<string | undefined>();

  useEffect(() => {
    if (type && !needLocation && !booking.slots && !booking.busy)
      void actions.loadSlots(from, DAYS);
  }, [type?.id, needLocation, booking.slots, booking.locationId]);

  const timeFmt = new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: patientTz,
  });
  const errorText =
    booking.error === "taken"
      ? t(locale, "booking.taken")
      : booking.error === "contact"
        ? t(locale, "booking.contactFirst")
        : booking.error
          ? t(locale, "booking.unavailable")
          : undefined;

  // Step 4: payment (paid types)
  if (booking.payment) {
    return (
      <Payment
        locale={locale}
        payment={booking.payment}
        onConfirmed={() => actions.awaitPaidConfirmation()}
        onFailed={actions.paymentFailed}
        onCancel={actions.cancelPayment}
      />
    );
  }

  // Step 0: procedures (optional, several allowed), grouped by category as in the catalogue.
  if (booking.options && catalogue.length > 0 && !booking.proceduresDone) {
    const groups = new Map<string, typeof catalogue>();
    for (const p of catalogue) groups.set(p.category, [...(groups.get(p.category) ?? []), p]);
    const toggle = (name: string) =>
      setPicked((cur) => (cur.includes(name) ? cur.filter((x) => x !== name) : [...cur, name]));
    return (
      <div class="in booking" role="group" aria-label={t(locale, "booking.chooseProcedures")}>
        <p class="sub">{t(locale, "booking.chooseProcedures")}</p>
        {[...groups.entries()].map(([cat, items]) => (
          <div key={cat} class="pgroup">
            {groups.size > 1 && <div class="pcat">{cat}</div>}
            <div class="chips">
              {items.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  class={`chip${picked.includes(p.name) ? " on" : ""}`}
                  aria-pressed={picked.includes(p.name)}
                  onClick={() => toggle(p.name)}
                >
                  {p.name}
                </button>
              ))}
            </div>
          </div>
        ))}
        <div class="row">
          <button
            type="button"
            class="primary"
            onClick={() => actions.chooseBookingProcedures(picked)}
          >
            {picked.length ? t(locale, "booking.continue") : t(locale, "booking.skipProcedures")}
          </button>
          <button type="button" class="link" onClick={actions.closeBooking}>
            {t(locale, "booking.notNow")}
          </button>
        </div>
      </div>
    );
  }

  // Step 1: type
  if (!type) {
    return (
      <div class="in booking" role="group" aria-label={t(locale, "booking.title")}>
        <p class="sub">{t(locale, "booking.chooseType")}</p>
        {booking.busy && !booking.options && <div class="spinner" aria-label="Loading" />}
        <div class="group">
          {(booking.options?.types ?? []).map((x) => (
            <button
              key={x.id}
              type="button"
              class="opt"
              disabled={!x.bookable}
              onClick={() => actions.chooseBookingType(x.id)}
            >
              <span class="opt-name">{x.name}</span>
              <span class="opt-meta">
                {x.durationMinutes} {t(locale, "booking.min")} ·{" "}
                {x.mode === "online" ? t(locale, "booking.online") : t(locale, "booking.inPerson")}{" "}
                ·{" "}
                {x.price === 0 ? t(locale, "booking.free") : fmtMoney(x.price, x.currency, locale)}
              </span>
              {!x.bookable && <span class="opt-note">{t(locale, "booking.paidSoon")}</span>}
              {x.bookable && x.price > 0 && (
                <span class="opt-note">{t(locale, "booking.payNote").split(".")[0]}.</span>
              )}
              {x.description && <span class="opt-desc">{x.description}</span>}
            </button>
          ))}
        </div>
        {errorText && <p class="err">{errorText}</p>}
        <div class="row">
          <button type="button" class="link" onClick={actions.closeBooking}>
            {t(locale, "booking.notNow")}
          </button>
        </div>
      </div>
    );
  }

  // Step 1b: location (several sites)
  if (needLocation) {
    return (
      <div class="in booking" role="group" aria-label={t(locale, "booking.chooseLocation")}>
        <p class="sub">{t(locale, "booking.chooseLocation")}</p>
        <div class="group">
          {locations.map((l) => (
            <button
              key={l.id}
              type="button"
              class="opt"
              onClick={() => actions.chooseBookingLocation(l.id)}
            >
              <span class="opt-name">{l.name}</span>
              <span class="opt-meta">{l.address}</span>
            </button>
          ))}
        </div>
        <div class="row">
          <button type="button" class="link" onClick={() => actions.chooseBookingType(undefined)}>
            {t(locale, "booking.back")}
          </button>
        </div>
      </div>
    );
  }

  // No specialist step: the clinic assigns the specialist (owner, 15 and 17 October 2026).
  // Step 3: day + time
  const days = booking.slots?.days ?? [];
  const firstWithSlots = days.find((d) => d.slots.length > 0) ?? days[0];
  const current = days.find((d) => d.date === selected && d.slots.length > 0) ?? firstWithSlots;
  const weekEmpty = days.every((d) => d.slots.length === 0);
  return (
    <div class="in booking" role="group" aria-label={t(locale, "booking.chooseTime")}>
      <p class="sub">
        <strong>{type.name}</strong>
        {locationId && locations.length > 1
          ? ` · ${locations.find((l) => l.id === locationId)?.name ?? ""}`
          : ""}
      </p>
      <div class="days" role="tablist">
        {days.map((d) => {
          const l = dayLabel(d.date, locale, clinicTz);
          const on = current?.date === d.date;
          return (
            <button
              key={d.date}
              type="button"
              role="tab"
              aria-selected={on}
              class={`day${on ? " on" : ""}`}
              disabled={d.slots.length === 0}
              onClick={() => setSelected(d.date)}
            >
              <span>{l.top}</span>
              <span class="small">{l.bottom}</span>
            </button>
          );
        })}
      </div>
      {booking.busy && <div class="spinner" aria-label="Loading" />}
      {!booking.busy && weekEmpty && <p class="muted">{t(locale, "booking.noSlotsWeek")}</p>}
      {!booking.busy && current && current.slots.length > 0 && (
        <div class="chips slots">
          {timesFor(current.slots).map((s) => (
            <button
              key={`${s.startAt}-${s.doctorId}`}
              type="button"
              class="chip"
              onClick={() =>
                void (type.price > 0
                  ? actions.holdAndPay(s.startAt, s.doctorId, atNodeId)
                  : actions.book(s.startAt, s.doctorId, atNodeId))
              }
            >
              {timeFmt.format(new Date(s.startAt))}
            </button>
          ))}
        </div>
      )}
      {patientTz !== clinicTz && (
        <p class="small muted">
          {t(locale, "booking.timesIn")} ({patientTz})
        </p>
      )}
      {errorText && <p class="err">{errorText}</p>}
      <div class="row">
        <button
          type="button"
          class="link"
          disabled={booking.busy}
          onClick={() => {
            setSelected(undefined);
            void actions.loadSlots(
              addDays(from, -DAYS) < today ? today : addDays(from, -DAYS),
              DAYS,
            );
          }}
        >
          ← {t(locale, "booking.earlier")}
        </button>
        <button
          type="button"
          class="link"
          disabled={booking.busy}
          onClick={() => {
            setSelected(undefined);
            void actions.loadSlots(addDays(from, DAYS), DAYS);
          }}
        >
          {t(locale, "booking.later")} →
        </button>
        <button
          type="button"
          class="link"
          onClick={() =>
            locations.length > 1
              ? actions.chooseBookingLocation(undefined)
              : actions.chooseBookingType(undefined)
          }
        >
          {t(locale, "booking.back")}
        </button>
      </div>
    </div>
  );
}

/** Confirmation bubble content after a booking (11.1 step 8). */
export function BookedBubble({
  locale,
  booked,
}: {
  locale: Locale;
  booked: NonNullable<BookingState["booked"]>;
}) {
  const patientTz = Intl.DateTimeFormat().resolvedOptions().timeZone || booked.timezone;
  const fmt = (tz: string) =>
    new Intl.DateTimeFormat(locale, {
      weekday: "long",
      day: "numeric",
      month: "long",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: tz,
      timeZoneName: "short",
    }).format(new Date(booked.startAt));
  const icsHref = booked.calendar
    ? `data:text/calendar;charset=utf-8,${encodeURIComponent(booked.calendar.ics)}`
    : undefined;
  return (
    <div class="booked">
      <strong>{t(locale, "booking.confirmed")}</strong>
      <div>
        {booked.typeName}
        {booked.doctorName ? ` ${t(locale, "booking.with")} ${booked.doctorName}` : ""} ·{" "}
        {booked.mode === "online" ? t(locale, "booking.online") : t(locale, "booking.inPerson")}
      </div>
      <div>{fmt(patientTz)}</div>
      {patientTz !== booked.timezone && <div class="small muted">{fmt(booked.timezone)}</div>}
      {booked.procedures && booked.procedures.length > 0 && (
        <div class="small">{booked.procedures.join(", ")}</div>
      )}
      {booked.location && <div class="small">{booked.location.name}</div>}
      {booked.address && <div class="small">{booked.address}</div>}
      {booked.meetingUrl && (
        <a class="link" href={booked.meetingUrl} target="_blank" rel="noopener">
          {booked.meetingUrl}
        </a>
      )}
      {booked.calendar && (
        <div class="row" style="justify-content:flex-start">
          <a class="link" href={booked.calendar.google} target="_blank" rel="noopener">
            {t(locale, "booking.addGoogle")}
          </a>
          <a class="link" href={icsHref} download="appointment.ics">
            {t(locale, "booking.addIcs")}
          </a>
        </div>
      )}
    </div>
  );
}
